/** @jsxImportSource @emotion/react */
/**
 * Компонент ячейки локации события
 * Переиспользуемый компонент для отображения локации цели события
 */
import React from 'react';
import { Typography, Tag } from 'antd';
import commonStyles from '../../../styles/commonStyles';
import type { BatchChildEvent } from '../../../types/plantingEvents';

const { Text } = Typography;

interface EventLocationCellProps {
  event: Pick<
    BatchChildEvent,
    | 'bedName'
    | 'plotName'
    | 'fieldName'
    | 'plantingId'
    | 'cropName'
    | 'bedId'
    | 'plotId'
    | 'fieldId'
  >;
  showTargetType?: boolean;
}

/**
 * Отображает локацию с приоритетом: Bed > Plot > Field > Planting
 */
export const EventLocationCell: React.FC<EventLocationCellProps> = ({
  event,
  showTargetType = false,
}) => {
  const getTargetTypeTag = () => {
    if (event.bedId) return <Tag color="blue">Грядка</Tag>;
    if (event.plotId) return <Tag color="green">Участок</Tag>;
    if (event.fieldId) return <Tag color="orange">Поле</Tag>;
    if (event.plantingId) return <Tag color="purple">Посадка</Tag>;
    return null;
  };

  const renderLocation = () => {
    // Приоритет: Bed > Plot > Field > Planting
    if (event.bedName) {
      return (
        <div>
          <Text strong>{event.bedName}</Text>
          {event.plotName && (
            <>
              <br />
              <Text type="secondary" css={commonStyles.smallText}>
                {event.plotName}
              </Text>
            </>
          )}
          {event.fieldName && (
            <>
              <br />
              <Text type="secondary" css={commonStyles.smallText}>
                {event.fieldName}
              </Text>
            </>
          )}
        </div>
      );
    }

    if (event.plotName) {
      return (
        <div>
          <Text strong>{event.plotName}</Text>
          {event.fieldName && (
            <>
              <br />
              <Text type="secondary" css={commonStyles.smallText}>
                {event.fieldName}
              </Text>
            </>
          )}
        </div>
      );
    }

    if (event.fieldName) {
      return <Text strong>{event.fieldName}</Text>;
    }

    // Fallback для посадок без локации
    if (event.plantingId) {
      return (
        <div>
          <Text type="secondary">Посадка #{event.plantingId}</Text>
          {event.cropName && (
            <>
              <br />
              <Text type="secondary" css={commonStyles.tinyText}>
                {event.cropName}
              </Text>
            </>
          )}
        </div>
      );
    }

    return <Text type="secondary">—</Text>;
  };

  return (
    <div>
      {renderLocation()}
      {showTargetType && getTargetTypeTag()}
    </div>
  );
};
